import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { signOut, getLecturerSessions, getAttendance } from '../lib/supabase'
import { exportAttendance } from '../lib/export'

export default function LecturerPortal() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const [assignments, setAssignments] = useState([])
  const [selectedOrgId, setSelectedOrgId] = useState(null)
  const [selectedSession, setSelectedSession] = useState(null)
  const [attendance, setAttendance] = useState([])
  const [attendanceLoading, setAttendanceLoading] = useState(false)
  const [dataLoading, setDataLoading] = useState(true)
  const [exportFilters, setExportFilters] = useState({ dateFrom: '', dateTo: '', format: 'xlsx' })
  const [tab, setTab] = useState('attendance')

  useEffect(() => { if (!loading && !user) navigate('/login') }, [loading, user, navigate])

  useEffect(() => {
    async function load() {
      if (!user) return
      const { data } = await getLecturerSessions(user.id)
      const list = data || []
      setAssignments(list)
      // Default to first org
      if (list.length > 0) {
        const firstOrgId = list[0].sessions?.org_id
        setSelectedOrgId(firstOrgId)
        setSelectedSession(list[0].sessions)
      }
      setDataLoading(false)
    }
    load()
  }, [user])

  // Group assignments by org
  const orgGroups = useMemo(() => {
    const map = {}
    assignments.forEach(a => {
      const org = a.sessions?.organizations
      const orgId = a.sessions?.org_id
      if (!orgId) return
      if (!map[orgId]) map[orgId] = { org, orgId, sessions: [] }
      if (a.sessions) map[orgId].sessions.push(a.sessions)
    })
    return Object.values(map)
  }, [assignments])

  const activeOrg = orgGroups.find(g => g.orgId === selectedOrgId)
  const activeSessions = activeOrg?.sessions || []

  useEffect(() => {
    async function loadAttendance() {
      if (!selectedSession) return
      setAttendanceLoading(true)
      const { data } = await getAttendance(selectedSession.org_id, { sessionId: selectedSession.id, limit: 500 })
      setAttendance(data || [])
      setAttendanceLoading(false)
    }
    loadAttendance()
  }, [selectedSession])

  function switchOrg(orgId) {
    setSelectedOrgId(orgId)
    const group = orgGroups.find(g => g.orgId === orgId)
    if (group?.sessions.length > 0) {
      setSelectedSession(group.sessions[0])
      setTab('attendance')
    }
  }

  if (loading || dataLoading) return (
    <div className="page-center"><span className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} /></div>
  )

  const org = activeOrg?.org
  const L = org ? {
    member: org.member_label || 'Member',
    idField: org.id_field_label || 'ID',
    session: org.session_label || 'Session',
    attendance: org.attendance_label || 'Attendance',
  } : { member: 'Member', idField: 'ID', session: 'Session', attendance: 'Attendance' }

  const today = new Date().toISOString().slice(0, 10)
  const todayPresent = attendance.filter(a => a.marked_at?.startsWith(today) && a.status === 'present')
  const overrides = attendance.filter(a => a.status === 'override')
  const totalPresent = attendance.filter(a => a.status === 'present').length

  function getFiltered() {
    return attendance.filter(a => {
      if (a.status !== 'present' && a.status !== 'override') return false
      if (exportFilters.dateFrom && a.marked_at < exportFilters.dateFrom + 'T00:00:00') return false
      if (exportFilters.dateTo && a.marked_at > exportFilters.dateTo + 'T23:59:59') return false
      return true
    })
  }

  return (
    <div className="page">
      <nav className="topnav">
        <div className="flex items-center gap-2">
          <div className="logo">Campus<span>Flow</span></div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', borderLeft: '1px solid var(--border)', paddingLeft: 12 }}>
            Lecturer view
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="badge badge-purple">View only</span>
          <button className="btn btn-ghost btn-sm" onClick={async () => { await signOut(); navigate('/') }}>Sign out</button>
        </div>
      </nav>

      <div className="container" style={{ paddingTop: '1.5rem', paddingBottom: '3rem' }}>
        {assignments.length === 0 ? (
          <div className="card text-center" style={{ maxWidth: 460, margin: '4rem auto' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>👨‍🏫</div>
            <div className="card-title mb-2">You're in — waiting for session assignment</div>
            <div className="card-sub mb-3">
              Your invite was accepted. Your administrator needs to assign you to sessions before data appears here.
            </div>
            <div className="alert alert-info" style={{ textAlign: 'left', fontSize: 13 }}>
              <strong>Tell your admin:</strong> Go to Admin → Lecturers tab → click <strong>↻ Refresh</strong> → then <strong>Assign sessions</strong> next to your name.
            </div>
          </div>
        ) : (
          <>
            {/* ── Org switcher (shown only if assigned to multiple orgs) ── */}
            {orgGroups.length > 1 && (
              <div className="card mb-4">
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Your organisations
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {orgGroups.map(g => (
                    <button
                      key={g.orgId}
                      onClick={() => switchOrg(g.orgId)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 'var(--radius)',
                        border: `2px solid ${g.orgId === selectedOrgId ? 'var(--blue)' : 'var(--border)'}`,
                        background: g.orgId === selectedOrgId ? 'var(--blue-light)' : 'transparent',
                        color: g.orgId === selectedOrgId ? 'var(--blue)' : 'var(--text)',
                        fontWeight: g.orgId === selectedOrgId ? 700 : 500,
                        fontSize: 14,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {g.org?.name || 'Unknown org'}
                      <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.7 }}>
                        ({g.sessions.length} {g.sessions.length === 1 ? 'session' : 'sessions'})
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Session selector within the active org ── */}
            {activeOrg && (
              <div className="card mb-4">
                <div className="flex items-center gap-3" style={{ flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, flexShrink: 0 }}>
                    {orgGroups.length > 1 ? `${org?.name} —` : ''} {L.session}:
                  </div>
                  {activeSessions.length === 1 ? (
                    <div style={{ fontWeight: 600, color: 'var(--blue)' }}>{activeSessions[0].name}</div>
                  ) : (
                    <select
                      value={selectedSession?.id || ''}
                      onChange={e => {
                        const s = activeSessions.find(s => s.id === e.target.value)
                        setSelectedSession(s)
                      }}
                      style={{ maxWidth: 300 }}
                    >
                      {activeSessions.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name}{s.group_name ? ` — ${s.group_name}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {selectedSession && (
                    <div className="text-sm text-muted">
                      {selectedSession.start_time}–{selectedSession.end_time} · {(selectedSession.days || []).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Metrics ── */}
            <div className="metric-grid">
              <div className="metric">
                <div className="flex items-center gap-1 mb-1"><span className="live-dot" /><span className="text-xs text-muted">TODAY</span></div>
                <div className="metric-val">{todayPresent.length}</div>
                <div className="metric-lbl">Present today</div>
              </div>
              <div className="metric">
                <div className="metric-val">{totalPresent}</div>
                <div className="metric-lbl">Total check-ins</div>
              </div>
              <div className="metric">
                <div className="metric-val" style={{ color: overrides.length > 0 ? 'var(--amber)' : 'var(--text)' }}>{overrides.length}</div>
                <div className="metric-lbl">Admin overrides</div>
              </div>
              {orgGroups.length > 1 && (
                <div className="metric">
                  <div className="metric-val" style={{ color: 'var(--blue)' }}>{orgGroups.length}</div>
                  <div className="metric-lbl">Organisations</div>
                </div>
              )}
            </div>

            {/* ── Tabs ── */}
            <div className="tab-bar">
              <button className={`tab-btn ${tab === 'attendance' ? 'active' : ''}`} onClick={() => setTab('attendance')}>
                {L.attendance} log
              </button>
              <button className={`tab-btn ${tab === 'overrides' ? 'active' : ''}`} onClick={() => setTab('overrides')}>
                Overrides {overrides.length > 0 && `(${overrides.length})`}
              </button>
              <button className={`tab-btn ${tab === 'export' ? 'active' : ''}`} onClick={() => setTab('export')}>
                Export
              </button>
            </div>

            {/* ── Attendance log ── */}
            {tab === 'attendance' && (
              <div className="fade-up">
                {attendanceLoading ? (
                  <div className="card text-center" style={{ padding: '2rem' }}>
                    <span className="spinner" style={{ width: 24, height: 24, borderWidth: 2 }} />
                  </div>
                ) : totalPresent === 0 ? (
                  <div className="card"><div className="empty-state"><div className="empty-state-title">No records yet</div><div className="empty-state-sub">Records appear here as members check in</div></div></div>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr><th>Name</th><th>{L.idField}</th><th>Date</th><th>Time</th><th>GPS</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {attendance.filter(a => a.status === 'present' || a.status === 'override').slice(0, 200).map(a => (
                          <tr key={a.id}>
                            <td style={{ fontWeight: 500 }}>{a.members?.name || a.member_name || '—'}</td>
                            <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{a.member_uid || '—'}</td>
                            <td>{new Date(a.marked_at).toLocaleDateString()}</td>
                            <td>{new Date(a.marked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                            <td><span className={`badge ${a.gps_verified ? 'badge-green' : 'badge-gray'}`}>{a.gps_verified ? 'OK' : '—'}</span></td>
                            <td><span className={`badge ${a.status === 'present' ? 'badge-green' : 'badge-amber'}`}>{a.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── Overrides ── */}
            {tab === 'overrides' && (
              <div className="fade-up">
                {overrides.length === 0 ? (
                  <div className="card"><div className="empty-state"><div className="empty-state-title">No overrides recorded</div><div className="empty-state-sub">Manual adjustments by an admin appear here with their reason</div></div></div>
                ) : (
                  <>
                    <div className="alert alert-warning mb-3">These records were manually adjusted by an administrator.</div>
                    <div className="table-wrap">
                      <table>
                        <thead><tr><th>Name</th><th>{L.idField}</th><th>Date</th><th>Marked as</th><th>Reason</th></tr></thead>
                        <tbody>
                          {overrides.map(a => (
                            <tr key={a.id}>
                              <td style={{ fontWeight: 500 }}>{a.members?.name || a.member_name || '—'}</td>
                              <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{a.member_uid || '—'}</td>
                              <td>{new Date(a.marked_at).toLocaleDateString()}</td>
                              <td><span className="badge badge-amber">override</span></td>
                              <td style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 200 }}>{a.override_reason || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Export ── */}
            {tab === 'export' && (
              <div className="fade-up">
                <div className="card">
                  <div className="card-title mb-1">Export {L.attendance?.toLowerCase()}</div>
                  <div className="card-sub mb-3">Download records for <strong>{selectedSession?.name}</strong></div>
                  <div className="grid-2">
                    <div className="form-group"><label className="field-label">From date</label><input type="date" value={exportFilters.dateFrom} onChange={e => setExportFilters({ ...exportFilters, dateFrom: e.target.value })} /></div>
                    <div className="form-group"><label className="field-label">To date</label><input type="date" value={exportFilters.dateTo} onChange={e => setExportFilters({ ...exportFilters, dateTo: e.target.value })} /></div>
                  </div>
                  <div className="form-group">
                    <label className="field-label">Format</label>
                    <select value={exportFilters.format} onChange={e => setExportFilters({ ...exportFilters, format: e.target.value })}>
                      <option value="xlsx">Excel (.xlsx)</option>
                      <option value="csv">CSV (.csv)</option>
                    </select>
                  </div>
                  <div className="flex gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
                    {[{ label: 'Today', days: 0 }, { label: 'This week', days: 7 }, { label: 'This month', days: 30 }, { label: 'Full semester', days: 150 }].map(r => (
                      <button key={r.label} className="btn btn-secondary btn-sm" onClick={() => {
                        const to = new Date().toISOString().slice(0, 10)
                        const from = new Date(Date.now() - r.days * 86400000).toISOString().slice(0, 10)
                        setExportFilters(f => ({ ...f, dateFrom: r.days === 0 ? to : from, dateTo: to }))
                      }}>{r.label}</button>
                    ))}
                  </div>
                  <button className="btn btn-primary" onClick={() => exportAttendance(getFiltered(), org, exportFilters.format, `attendance-${selectedSession?.name?.replace(/\s+/g, '-').toLowerCase()}`)}>
                    Download {exportFilters.format.toUpperCase()} ({getFiltered().length} records)
                  </button>
                  <p className="field-hint mt-2">Includes name, {L.idField?.toLowerCase()}, date, time, GPS status, and override details.</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
